import { useEffect, useState } from 'react';
import { apiGetAllGRNs, mapGRNToMockGRN } from '@/api/grn.api';
import { useWarehouseStore } from '@/stores/warehouseStore';

export interface GRN {
  id: string;
  grnNumber: string;
  supplierId: string;
  supplierName: string;
  date: string;
  warehouseName: string;
  items: GRNItem[];
  totalValue: number;
  piCreated?: boolean;
}
// In GRNSelector.tsx — update GRNItem to match actual API fields
export interface GRNItem {
  itemId: string;
  itemName: string;
  quantity: number;   // ← API uses `quantity`, not `qty`
  qty?: number;       // keep for backwards compat
  rate: number;
  total: number;      // ← API uses `total`
  unitName?: string;
  hsnCode?: string;
  barcode?: string;
  companyBarcode?: string;
}


interface Props {
  supplierId: string;
  selectedGRNId: string | null;
  onSelect: (grn: GRN | null) => void;
}

function formatINR(n: number) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}

export default function GRNSelector({ supplierId, selectedGRNId, onSelect }: Props) {
  const [grns, setGrns] = useState<GRN[]>([]);
  const [loading, setLoading] = useState(false);
  const { selectedWarehouseId } = useWarehouseStore();

  useEffect(() => {
    if (!supplierId) {
      setGrns([]);
      return;
    }

    const fetchGRNs = async () => {
      setLoading(true);
      try {
        const res = await apiGetAllGRNs({
          supplier_id: supplierId,
          warehouse_id:
            selectedWarehouseId && selectedWarehouseId !== 'ALL'
              ? selectedWarehouseId
              : undefined,
        });

        const list = (res?.data || []).filter((g: any) => {
          const matchesSupplier =
            g.supplierId === supplierId ||
            g.supplier_id === supplierId ||
            !supplierId;
          const isNotLinked = !g.piCreated && (!g.invoiceId || g.invoiceId === null);
          const isConfirmed = g.status === 'CONFIRMED' || g.status === 'APPROVED';
          return matchesSupplier && isNotLinked && isConfirmed;
        });

        setGrns(list as any);
      } catch (err) {
        console.error('Failed to fetch GRNs for supplier', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGRNs();
  }, [supplierId, selectedWarehouseId]);

  if (!supplierId) return null;

  return (
    <div className="mt-3 p-3 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-1.5">
          <i className="ri-inbox-archive-line text-indigo-600" />
          Available GRNs from this supplier
        </p>
        {loading && <i className="ri-loader-4-line animate-spin text-xs text-indigo-600" />}
      </div>

      <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
        {/* None option */}
        <label
          className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
            selectedGRNId === null
              ? 'bg-white border-indigo-500 ring-1 ring-indigo-500/20 shadow-sm'
              : 'bg-white/70 border-indigo-100 hover:border-indigo-300'
          }`}
        >
          <input
            type="radio"
            name="grn-select"
            checked={selectedGRNId === null}
            onChange={() => onSelect(null)}
            className="mt-0.5 accent-indigo-600 cursor-pointer"
          />
          <div>
            <span className="text-xs font-bold text-slate-800">
              None — Direct Purchase Invoice
            </span>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Stock will be updated on save
            </p>
          </div>
        </label>

        {/* List of GRNs */}
        {grns.map((grn) => {
          const items = Array.isArray(grn.items) ? grn.items : [];
          const calculatedQty = items.reduce(
            (s, i) => s + Number(i.quantity ?? i.qty ?? 0),
            0
          );
          const totalQty = calculatedQty > 0
            ? calculatedQty
            : Number((grn as any).totalItemsQty ?? (grn as any).totalQty ?? (grn as any).total_items_qty ?? (grn as any).total_qty ?? 0);

          const itemCount = items.length > 0
            ? items.length
            : Number((grn as any).itemCount ?? (grn as any).item_count ?? 0);

          const isSelected = selectedGRNId === grn.id;
          const displayDate = grn.date
            ? new Date(grn.date).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : '';

          return (
            <label
              key={grn.id}
              className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? 'bg-white border-indigo-500 ring-1 ring-indigo-500/20 shadow-sm'
                  : 'bg-white/80 border-indigo-100 hover:border-indigo-300'
              }`}
            >
              <input
                type="radio"
                name="grn-select"
                checked={isSelected}
                onChange={() => onSelect(grn)}
                className="mt-0.5 accent-indigo-600 cursor-pointer"
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-indigo-700 font-mono">
                    {grn.grnNumber}
                  </span>
                  <span className="text-xs font-bold text-indigo-900">
                    {formatINR(Number(grn.totalValue || 0))}
                  </span>
                </div>

                <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                  {displayDate && <span>📅 {displayDate}</span>}
                  <span>📦 {itemCount} items ({totalQty} units)</span>
                  {grn.warehouseName && <span>🏬 {grn.warehouseName}</span>}
                </div>
              </div>
            </label>
          );
        })}

        {!loading && grns.length === 0 && (
          <p className="text-xs text-slate-400 italic p-1">No un-invoiced GRNs found for this supplier.</p>
        )}
      </div>
    </div>
  );
}


